// Feedback List Component
// Displays feedback conversations (filtering handled by parent)

import React from 'react';
import {
  Clock,
  CheckCircle,
  Archive,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Minus,
  MessageCircle,
  Calendar,
  User
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { featureRequestBadge, isConfirmedFeatureRequest } from '../utils/feedbackBuckets';
import { UnreadTitle, unreadRowCls } from './UnreadIndicator';
import type { FeedbackListProps, FeedbackStatus, FeedbackPriority } from '../types';

// Helper function to safely format dates
const safeFormatDate = (
  dateString: string | null | undefined,
  formatFn: (date: Date, options?: { addSuffix?: boolean }) => string,
  fallback: string = 'Unknown',
  options?: { addSuffix?: boolean }
): string => {
  if (!dateString) return fallback;

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return fallback;
    }
    return formatFn(date, options);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return fallback;
  }
};

const statusConfig = {
  open: {
    label: 'Open',
    color: [
      'bg-blue-100 dark:bg-blue-900',
      'text-blue-800 dark:text-blue-200',
      'border-blue-200 dark:border-blue-800'
    ].join(' '),
    icon: MessageCircle
  },
  in_progress: {
    label: 'In Progress',
    color: [
      'bg-yellow-100 dark:bg-yellow-900',
      'text-yellow-800 dark:text-yellow-200',
      'border-yellow-200 dark:border-yellow-800'
    ].join(' '),
    icon: Clock
  },
  resolved: {
    label: 'Resolved',
    color: [
      'bg-green-100 dark:bg-green-900',
      'text-green-800 dark:text-green-200',
      'border-green-200 dark:border-green-800'
    ].join(' '),
    icon: CheckCircle
  },
  archived: {
    label: 'Archived',
    color: [
      'bg-gray-100 dark:bg-gray-800',
      'text-gray-800 dark:text-gray-200',
      'border-gray-200 dark:border-gray-700'
    ].join(' '),
    icon: Archive
  }
};

const priorityConfig = {
  low: { label: 'Low', color: 'text-gray-900 dark:text-gray-100', icon: ArrowDown },
  medium: { label: 'Medium', color: 'text-blue-700 dark:text-blue-400', icon: Minus },
  high: { label: 'High', color: 'text-orange-700 dark:text-orange-400', icon: ArrowUp },
  urgent: { label: 'Urgent', color: 'text-red-700 dark:text-red-400', icon: AlertTriangle }
};

export const FeedbackList: React.FC<FeedbackListProps> = ({
  conversations,
  onConversationSelect,
  onStatusUpdate,
  onPriorityUpdate,
  onPrefetchConversation,
  loading = false,
  canManage = false,
  unreadCounts = {}
}) => {
  if (loading) {
    return (
      <div className='space-y-4'>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className='h-24 w-full' />
        ))}
      </div>
    );
  }

  const unreadCountFor = (id: string) => unreadCounts[id] ?? 0;
  // Read-only badges say "Feature request"; managers keep the status select
  const getStatusInfo = (conversation: FeedbackListProps['conversations'][number]) =>
    isConfirmedFeatureRequest(conversation) && !(canManage && onStatusUpdate)
      ? featureRequestBadge
      : statusConfig[conversation.status];

  return (
    <div className='space-y-4'>
      {conversations.length === 0 ? (
        <Card>
          <CardContent className='py-12 text-center'>
            <MessageCircle className='h-12 w-12 mx-auto text-muted-foreground mb-4' />
            <h3 className='text-lg font-semibold mb-2'>No conversations found</h3>
            <p className='text-muted-foreground'>Try adjusting your filters or search terms.</p>
          </CardContent>
        </Card>
      ) : (
        conversations.map(conversation => {
          const isFeatureRequest = isConfirmedFeatureRequest(conversation);
          const statusInfo = getStatusInfo(conversation);
          const unreadCount = unreadCountFor(conversation.id);
          const priorityInfo = priorityConfig[conversation.priority] || priorityConfig.medium;
          const StatusIcon = statusInfo.icon;
          const PriorityIcon = priorityInfo.icon;

          return (
            <Card
              key={conversation.id}
              className={cn(
                'cursor-pointer hover:shadow-md transition-shadow',
                unreadRowCls(unreadCount)
              )}
              onClick={() => onConversationSelect(conversation.id)}
              onMouseEnter={() => onPrefetchConversation?.(conversation.id)}
            >
              <CardContent className='p-4'>
                <div className='space-y-4'>
                  {/* Header with Title and Status */}
                  <div className='flex items-start justify-between gap-3'>
                    <UnreadTitle
                      title={conversation.title}
                      unreadCount={unreadCount}
                      className='text-base'
                    />
                    {canManage && onStatusUpdate ? (
                      <Select
                        value={conversation.status}
                        onValueChange={(value: FeedbackStatus) => {
                          onStatusUpdate(conversation.id, value);
                        }}
                      >
                        <SelectTrigger
                          className={cn('w-28 h-7 text-xs shrink-0 shadow-sm', statusInfo.color)}
                          onClick={e => e.stopPropagation()}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(statusConfig).map(([status, config]) => (
                            <SelectItem
                              key={status}
                              value={status}
                              className={cn('rounded-md', config.color)}
                            >
                              <div className='flex items-center gap-2'>
                                <config.icon className='h-3 w-3' />
                                {config.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge
                        variant='outline'
                        className={cn('text-xs flex items-center gap-1 shrink-0', statusInfo.color)}
                      >
                        <StatusIcon className='h-3 w-3' />
                        {statusInfo.label}
                      </Badge>
                    )}
                  </div>

                  {/* User and Time Info */}
                  <div className='flex items-center justify-between'>
                    <div
                      className={cn(
                        'flex items-center gap-1 text-xs',
                        'text-muted-foreground min-w-0 flex-1'
                      )}
                    >
                      <User className='h-3 w-3 shrink-0' />
                      <span className='truncate'>
                        {conversation.user_full_name || conversation.user_email || 'Unknown user'}
                        {conversation.tenant_name && (
                          <span className='text-muted-foreground/60'>
                            {' '}
                            ({conversation.tenant_name})
                          </span>
                        )}
                      </span>
                    </div>
                    <div
                      className={cn(
                        'flex items-center gap-1 text-xs',
                        'text-muted-foreground shrink-0 ml-2'
                      )}
                    >
                      <Calendar className='h-3 w-3' />
                      <span className='whitespace-nowrap'>
                        {safeFormatDate(
                          conversation.updated_at,
                          formatDistanceToNow,
                          'Unknown time',
                          { addSuffix: true }
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Bottom Row: Priority, Category, and Messages */}
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      {/* Priority indicator/dropdown */}
                      {canManage && onPriorityUpdate ? (
                        <Select
                          value={conversation.priority}
                          onValueChange={(value: FeedbackPriority) => {
                            onPriorityUpdate(conversation.id, value);
                          }}
                        >
                          <SelectTrigger
                            className='w-24 h-7 text-xs border-primary/30 bg-background shadow-sm'
                            onClick={e => e.stopPropagation()}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(priorityConfig).map(([priority, config]) => {
                              const Icon = config.icon;
                              return (
                                <SelectItem key={priority} value={priority}>
                                  <div className={cn('flex items-center gap-1.5', config.color)}>
                                    <Icon className='h-3 w-3' />
                                    <span className='font-medium'>{config.label}</span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className={cn('flex items-center gap-1', priorityInfo.color)}>
                          <PriorityIcon className='h-3 w-3' />
                          <span className='text-xs font-semibold'>{priorityInfo.label}</span>
                        </div>
                      )}

                      {/* Category */}
                      {isFeatureRequest ? (
                        <Badge
                          variant='outline'
                          className={cn(
                            'text-xs flex items-center gap-1',
                            featureRequestBadge.color
                          )}
                        >
                          <featureRequestBadge.icon className='h-3 w-3' />
                          {featureRequestBadge.label}
                        </Badge>
                      ) : (
                        conversation.category_display_name && (
                          <Badge variant='secondary' className='text-xs'>
                            {conversation.category_display_name}
                          </Badge>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default FeedbackList;
